"use client";

import { useState, useMemo, useEffect } from "react";
import { useSearchParamsOrEmpty } from "@/hooks/use-search-params-or-empty";
import { createBrowserClient } from "@supabase/ssr";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert } from "@/components/ui/alert";
import { Shield, ArrowLeft } from "lucide-react";
import {
  getPostAuthRedirect,
  sanitizeNextPath,
} from "@/lib/auth/redirect";

type AuthMode = "sign-in" | "sign-up" | "forgot-password" | "sso";

function getRequestedMode(mode: string | null): AuthMode | null {
  switch (mode) {
    case "sign-up":
    case "forgot-password":
    case "sso":
    case "sign-in":
      return mode;
    default:
      return null;
  }
}

/** Map raw Supabase auth errors to user-friendly messages */
function friendlyAuthError(raw: string): string {
  const lower = raw.toLowerCase();
  if (lower.includes("invalid login credentials") || lower.includes("invalid_credentials")) {
    return "Invalid email or password. Please try again.";
  }
  if (lower.includes("email not confirmed")) {
    return "Please verify your email before signing in. Check your inbox for the confirmation link.";
  }
  if (lower.includes("too many requests") || lower.includes("rate limit")) {
    return "Too many login attempts. Please wait a moment and try again.";
  }
  if (lower.includes("user not found") || lower.includes("no user found")) {
    return "No account found with that email address.";
  }
  if (lower.includes("signup is disabled") || lower.includes("signups not allowed")) {
    return "Sign-ups are currently disabled. Contact your administrator.";
  }
  if (lower.includes("timed out")) {
    return "Sign in timed out. Please check your connection and try again.";
  }
  // Pass through anything else — at least the user sees something
  return raw || "An unexpected error occurred. Please try again.";
}

export function AuthForm() {
  const searchParams = useSearchParamsOrEmpty();
  const requestedMode = getRequestedMode(searchParams.get("mode"));
  const nextParam = searchParams.get("next");
  const safeNext = sanitizeNextPath(nextParam);
  const postAuthRedirect = getPostAuthRedirect(nextParam);
  const resetSuccessful = searchParams.get("reset") === "success";

  const [mode, setMode] = useState<AuthMode>(requestedMode ?? "sign-in");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [ssoEmail, setSsoEmail] = useState("");
  const [checkingSso, setCheckingSso] = useState(false);

  const supabase = useMemo(
    () =>
      createBrowserClient(
        process.env.NEXT_PUBLIC_SUPABASE_URL || "https://placeholder.supabase.co",
        process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY || "placeholder",
        {
          auth: {
            autoRefreshToken: false,
            detectSessionInUrl: false,
          },
        }
      ),
    []
  );

  useEffect(() => {
    if (requestedMode) {
      setMode(requestedMode);
    }
  }, [requestedMode]);

  useEffect(() => {
    if (!resetSuccessful) return;
    setMode("sign-in");
    setError(null);
    setMessage("Password reset. Sign in with your new password.");
  }, [resetSuccessful]);

  // Redirect if already authenticated — server fetch avoids the client-side
  // _initialize() lock that can stall subsequent signInWithPassword calls
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/auth/me");
        if (cancelled || !res.ok) return;
        const { authUser } = await res.json();
        if (!cancelled && authUser) {
          window.location.href = postAuthRedirect;
        }
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [postAuthRedirect]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setMessage(null);

    try {
      if (mode === "sign-in") {
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error("Sign in timed out")), 10000)
        );

        try {
          const { data, error } = await Promise.race([
            supabase.auth.signInWithPassword({ email, password }),
            timeoutPromise,
          ]) as Awaited<ReturnType<typeof supabase.auth.signInWithPassword>>;

          if (error) throw error;
          if (!data.session) {
            throw new Error("No session returned from sign in");
          }
          window.location.href = postAuthRedirect;
          return;
        } catch (err) {
          const msg = err instanceof Error ? err.message : "";
          if (msg.includes("timed out")) {
            // Sign-in may have succeeded despite timeout — server check
            // avoids re-entering the client _initialize() lock
            try {
              const res = await fetch("/api/auth/me");
              if (res.ok) {
                const { authUser } = await res.json();
                if (authUser) {
                  window.location.href = postAuthRedirect;
                  return;
                }
              }
            } catch {}
          }
          throw err;
        }
      } else if (mode === "sign-up") {
        const callbackUrl = new URL("/auth/callback", window.location.origin);
        if (safeNext) {
          callbackUrl.searchParams.set("next", safeNext);
        }

        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: callbackUrl.toString(),
          },
        });
        if (error) throw error;
        setMessage("Check your email for the confirmation link.");
      } else if (mode === "forgot-password") {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/auth/reset-password`,
        });
        if (error) throw error;
        setMessage("Check your email for the password reset link.");
      }
    } catch (err) {
      const raw = err instanceof Error ? err.message : String(err || "An error occurred");
      setError(friendlyAuthError(raw));
    } finally {
      setLoading(false);
    }
  };

  const handleOAuthSignIn = async (provider: "google" | "github") => {
    setLoading(true);
    setError(null);

    try {
      const callbackUrl = new URL("/auth/callback", window.location.origin);
      if (safeNext) {
        callbackUrl.searchParams.set("next", safeNext);
      }

      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: callbackUrl.toString(),
        },
      });
      if (error) throw error;
    } catch (err) {
      const raw = err instanceof Error ? err.message : String(err || "An error occurred");
      setError(friendlyAuthError(raw));
      setLoading(false);
    }
  };

  const handleSsoLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setCheckingSso(true);

    try {
      const domain = ssoEmail.split("@")[1]?.toLowerCase();
      if (!domain) {
        setError("Please enter a valid email address");
        return;
      }

      const res = await fetch(`/api/auth/sso/check?domain=${encodeURIComponent(domain)}`);
      if (!res.ok) throw new Error("Failed to check SSO availability");

      const data = await res.json();
      if (data.sso_enabled) {
        // Redirect to SP-initiated SSO login
        const ssoUrl = new URL("/api/auth/saml/login", window.location.origin);
        ssoUrl.searchParams.set("domain", domain);
        if (safeNext) {
          ssoUrl.searchParams.set("next", safeNext);
        }
        window.location.href = ssoUrl.toString();
        return;
      } else {
        setError("SSO is not enabled for this email domain. Contact your administrator.");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setCheckingSso(false);
    }
  };

  const heading = {
    "sign-in": "Welcome back",
    "sign-up": "Create an account",
    "sso": "Sign in with SSO",
    "forgot-password": "Reset your password",
  }[mode];

  const subtitle = {
    "sign-in": "Sign in to your Fluxity account",
    "sign-up": "Get your data into your system.",
    "sso": "Enter your work email to continue",
    "forgot-password": "We\u2019ll send you a reset link",
  }[mode];

  return (
    <div className="w-full max-w-sm mx-auto relative z-10">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-2xl font-semibold tracking-tight">{heading}</h1>
        <p className="text-sm text-muted-foreground mt-1.5">{subtitle}</p>
      </div>

      {/* Alerts */}
      {error && (
        <Alert
          variant="error"
          className="mb-5"
          dismissible
          onDismiss={() => setError(null)}
        >
          {error}
        </Alert>
      )}
      {message && (
        <Alert
          variant="success"
          className="mb-5"
          dismissible
          onDismiss={() => setMessage(null)}
        >
          {message}
        </Alert>
      )}

      {/* SSO Form */}
      {mode === "sso" ? (
        <form onSubmit={handleSsoLogin} className="space-y-4">
          <Input
            label="Work Email"
            type="email"
            value={ssoEmail}
            onChange={(e) => setSsoEmail(e.target.value)}
            placeholder="you@yourcompany.com"
            required
            autoComplete="email"
          />
          <Button type="submit" fullWidth loading={checkingSso}>
            Continue with SSO
          </Button>
          <div className="text-center pt-1">
            <button
              type="button"
              onClick={() => { setMode("sign-in"); setError(null); }}
              className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="size-3.5" />
              Back to sign in
            </button>
          </div>
        </form>
      ) : (
      <>
      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@company.com"
          required
          autoComplete="email"
        />

        {mode !== "forgot-password" && (
          <Input
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter your password"
            required
            autoComplete={mode === "sign-in" ? "current-password" : "new-password"}
            hint={
              mode === "sign-up"
                ? "Must be at least 8 characters"
                : undefined
            }
          />
        )}

        {mode === "sign-in" && (
          <div className="flex justify-end -mt-1">
            <button
              type="button"
              onClick={() => setMode("forgot-password")}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              Forgot password?
            </button>
          </div>
        )}

        <Button type="submit" fullWidth loading={loading} className="mt-1">
          {mode === "sign-in"
            ? "Sign in"
            : mode === "sign-up"
            ? "Create account"
            : "Send reset link"}
        </Button>
      </form>

      {/* OAuth */}
      {mode !== "forgot-password" && (
        <>
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center">
              <span className="px-3 bg-background text-xs text-muted-foreground uppercase tracking-wider">
                or
              </span>
            </div>
          </div>

          <div className="space-y-2.5">
            <Button
              type="button"
              variant="outline"
              fullWidth
              onClick={() => handleOAuthSignIn("google")}
              disabled={loading}
              className="h-10"
            >
              <svg className="size-4 mr-2.5" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              Continue with Google
            </Button>
            {/* SSO Button */}
            {mode === "sign-in" && (
              <Button
                type="button"
                variant="outline"
                fullWidth
                onClick={() => { setMode("sso"); setError(null); setMessage(null); }}
                disabled={loading}
                className="h-10"
              >
                <Shield className="size-4 mr-2.5" />
                Continue with SSO
              </Button>
            )}
          </div>
        </>
      )}
      </>
      )}

      {/* Mode Toggle */}
      <div className="mt-8 text-center text-sm">
        {mode === "sign-in" ? (
          <p className="text-muted-foreground">
            Don&apos;t have an account?{" "}
            <button
              type="button"
              onClick={() => setMode("sign-up")}
              className="text-foreground font-medium hover:underline"
            >
              Sign up
            </button>
          </p>
        ) : mode === "sign-up" ? (
          <p className="text-muted-foreground">
            Already have an account?{" "}
            <button
              type="button"
              onClick={() => setMode("sign-in")}
              className="text-foreground font-medium hover:underline"
            >
              Sign in
            </button>
          </p>
        ) : mode !== "sso" ? (
          <button
            type="button"
            onClick={() => setMode("sign-in")}
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="size-3.5" />
            Back to sign in
          </button>
        ) : null}
      </div>
    </div>
  );
}
